import React, {useState} from "react";
import {Table, TableBody, TableRow} from "@mui/material";
import {faTrash} from "@fortawesome/free-solid-svg-icons";

import {StandardLayout} from "../../../../components/StandardLayout";

import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {AddressInput} from "../../../../designSystem/components/AddressInput";
import {AgreeTerms} from "../../../../designSystem/components/AgreeTerms";
import {Badge} from "../../../../designSystem/components/Badge";
import {BaseAutocomplete} from "../../../../designSystem/components/BaseAutocomplete";
import {BaseInput} from "../../../../designSystem/components/BaseInput";
import {BorderedTableCell, BorderedTableCellTitle} from "../../../../designSystem/components/BaseTable";
import {BaseTextarea} from "../../../../designSystem/components/BaseTextarea";
import {BigButton} from "../../../../designSystem/components/BigButton";
import {BottomNavbarButton} from "../../../../designSystem/components/BottomNavbarButton";
import {LeftChatBubbleView, RightChatBubbleView} from "../../../../designSystem/components/ChatBubbleView";
import {CheckButton} from "../../../../designSystem/components/CheckButton";
import {DashboardCard} from "../../../../designSystem/components/DashboardCard";
import {DataCardLayout} from "../../../../designSystem/components/DataCardLayout";
import {DateInput} from "../../../../designSystem/components/DateInput";
import {Dialog} from "../../../../designSystem/components/Dialog";
import {Divider} from "../../../../designSystem/components/Divider";
import {Dropdown} from "../../../../designSystem/components/Dropdown";
import {FilterBox} from "../../../../designSystem/components/FilterBox";
import {GroupBadge} from "../../../../designSystem/components/GroupBadge";
import {HighlightedText} from "../../../../designSystem/components/HighlightedText";
import {LinkButton} from "../../../../designSystem/components/LinkButton";
import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {OptionButton} from "../../../../designSystem/components/OptionButton";
import {OrganizationCard} from "../../../../designSystem/components/OrganizationCard";
import {Paragraph} from "../../../../designSystem/components/Paragraph";
import {PhoneInput} from "../../../../designSystem/components/PhoneInput";
import {RoleBadge} from "../../../../designSystem/components/RoleBadge";
import {SearchBar} from "../../../../designSystem/components/SearchBar";
import {StatusSelector} from "../../../../designSystem/components/StatusSelector";
import {Tooltip} from "../../../../designSystem/components/Tooltip";
import {TwoButtonDialog} from "../../../../designSystem/components/TwoButtonDialog";
import {Typography} from "../../../../designSystem/components/Typography";
import {VaccineButtons} from "../../../../designSystem/components/VaccineButtons";

import {REGION_OPTIONS} from "../../../../utils/constants";

type BoardTab = 'designSystem' | 'components' | 'widgets';

type CatalogItem = {
  name: string;
  path: string;
}

const REGIONS = REGION_OPTIONS.map(r => r.value).filter(r => r !== '不限').map(r => `屏東縣${r}`)
const DEFAULT_COVER_IMAGE = 'https://hscnews.usc.edu/wp-content/uploads/2020/09/shutterstock_1415786840.jpg'
const AUTOCOMPLETE_OPTIONS = [
  {id: '1', name: '屏東市'},
  {id: '2', name: '潮州鎮'},
  {id: '3', name: '東港鎮'},
]

const SHARED_COMPONENTS: CatalogItem[] = [
  {name: 'BackdropOverlay', path: 'src/components/BackdropOverlay'},
  {name: 'CustomDialog', path: 'src/components/DialogCustom'},
  {name: 'LoadingDialog', path: 'src/components/DialogLoading'},
  {name: 'ImageUploader', path: 'src/components/ImageUploader'},
  {name: 'LongAnswer', path: 'src/components/LongAnswer'},
  {name: 'MeasurementChart', path: 'src/components/MeasurementChart'},
  {name: 'MeasurementTable', path: 'src/components/MeasurementTable'},
  {name: 'MultiSelection', path: 'src/components/MultiSelection'},
  {name: 'PaginationBar', path: 'src/components/PaginationBar'},
  {name: 'PermissionGuard', path: 'src/components/PermissionGuard'},
  {name: 'PostList', path: 'src/components/PostList'},
  {name: 'ShortAnswer', path: 'src/components/ShortAnswer'},
  {name: 'StandardLayout', path: 'src/components/StandardLayout'},
  {name: 'TotalView', path: 'src/components/TotalView'},
  {name: 'HeaderBar', path: 'src/components/layout/HeaderBar'},
  {name: 'FormPageLayout', path: 'src/components/layout/FormPageLayout'},
]

const SHARED_WIDGETS: CatalogItem[] = [
  {name: 'ApplicationDetail', path: 'src/widgets/ApplicationDetail'},
  {name: 'ApplicationSurvey', path: 'src/widgets/ApplicationSurvey'},
  {name: 'GoBackNavigationIconButton', path: 'src/widgets/GoBackNavigationIconButton'},
  {name: 'LoadingMessageView', path: 'src/widgets/LoadingMessageView'},
  {name: 'NextStepButton', path: 'src/widgets/NextStepButton'},
  {name: 'NoCasesMessageView', path: 'src/widgets/NoCasesMessageView'},
  {name: 'PlatformBottomNavbar', path: 'src/widgets/PlatformBottomNavbar'},
]

const Section = ({ title, children }: {title: string; children: React.ReactNode}) => (
  <div className="flex flex-col gap-2 pb-8 pt-4">
    <Typography variant="title">
      {title}
    </Typography>
    {children}
  </div>
)

const CatalogList = ({items}: {items: CatalogItem[]}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {items.map(item => (
      <div key={item.name} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <Typography variant="bodyBold">{item.name}</Typography>
        <Typography variant="captionMuted">{item.path}</Typography>
      </div>
    ))}
  </div>
)

export const StoryboardFeature = () => {
  const [boardTab, setBoardTab] = useState<BoardTab>('designSystem');
  const [addressInputValue, setAddressInputValue] = useState({ region: '', address: '' });
  const [agreeTermsValue, setAgreeTermsValue] = useState<boolean>(false);
  const [baseInputValue, setBaseInputValue] = useState<string>('');
  const [dropdownValue, setDropdownValue] = useState<string>('');
  const [phone, setPhone] = useState<string[]>([]);
  const [phonePrefix, setPhonePrefix] = useState<string[]>([]);
  const [autocompleteValue, setAutocompleteValue] = useState<{id: string; name: string} | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [yearRoc, setYearRoc] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');

  const renderDesignSystemBoard = () => (
    <React.Fragment>
      <Section title="ActionButton">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row flex-wrap gap-2">
            <ActionButton onClick={() => null} label="Filled Primary"/>
            <ActionButton variant="outline" onClick={() => null} label="Outline Primary"/>
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <ActionButton color="secondary" onClick={() => null} label="Filled Secondary"/>
            <ActionButton variant="outline" color="secondary" onClick={() => null} label="Outline Secondary"/>
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <ActionButton color="warning" onClick={() => null} label="Filled Warning"/>
            <ActionButton variant="outline" color="warning" onClick={() => null} label="Outline Warning"/>
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <ActionButton variant="outline" color="warning" disabled onClick={() => null} label="Disabled"/>
          </div>
        </div>
      </Section>
      <Section title="Typography">
        <div className="flex flex-col gap-2">
          <Typography variant="hero">hero</Typography>
          <Typography variant="title">title</Typography>
          <Typography variant="subtitle">subtitle</Typography>
          <Typography variant="body">body</Typography>
          <Typography variant="bodyBold">bodyBold</Typography>
          <Typography variant="caption">caption</Typography>
          <Typography variant="captionMuted">captionMuted</Typography>
          <Typography variant="error">error</Typography>
        </div>
      </Section>
      <Section title="AddressInput">
        <AddressInput onChange={(value) => setAddressInputValue(value)} values={addressInputValue} />
      </Section>
      <Section title="AgreeTerms">
        <AgreeTerms onClickLink={() => null} onChangeCheck={() => setAgreeTermsValue(!agreeTermsValue)} isChecked={agreeTermsValue} name={"某某條列"} />
      </Section>
      <Section title="Badge">
        <div className="flex flex-row gap-2">
          <Badge tone="gray">gray</Badge>
          <Badge tone="green">green</Badge>
          <Badge tone="red">red</Badge>
          <Badge tone="blue">blue</Badge>
          <Badge tone="amber">amber</Badge>
        </div>
      </Section>
      <Section title="BaseAutocomplete">
        <BaseAutocomplete
          label="行政區"
          options={AUTOCOMPLETE_OPTIONS}
          value={autocompleteValue}
          onChangeValue={value => setAutocompleteValue(value)}
        />
      </Section>
      <Section title="BaseInput">
        <BaseInput onChange={(e) => setBaseInputValue(e.target.value)} value={baseInputValue} />
      </Section>
      <Section title="BaseTable">
        <Table size="small">
          <TableBody>
            <TableRow>
              <BorderedTableCellTitle>標題</BorderedTableCellTitle>
              <BorderedTableCell>內文1</BorderedTableCell>
              <BorderedTableCell>內文2</BorderedTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>
      <Section title="BaseTextarea">
        <BaseTextarea onChange={(e) => setBaseInputValue(e.target.value)} value={baseInputValue} />
      </Section>
      <Section title="BigButton">
        <BigButton onClick={() => null} color="bg-[#6B5B95]" icon={faTrash} label="程澎聞" />
      </Section>
      <Section title="BottomNavbarButton">
        <div className="flex flex-row gap-2">
          <BottomNavbarButton onClick={() => null} label="程為龔" icon="fa-trash" selected={true}/>
          <BottomNavbarButton onClick={() => null} label="黃四倍" icon="fa-trash" selected={false}/>
        </div>
      </Section>
      <Section title="LeftChatBubbleView">
        <LeftChatBubbleView>
          Test Test Test
        </LeftChatBubbleView>
      </Section>
      <Section title="RightChatBubbleView">
        <RightChatBubbleView>
          Test Test Test
        </RightChatBubbleView>
      </Section>
      <Section title="CheckButton">
        <CheckButton label="林木川" selected={true} onClick={() => null} />
        <CheckButton label="姚莫利" selected={false} onClick={() => null} />
      </Section>
      <Section title="DashboardCard">
        <DashboardCard>
          <Typography variant="subtitle">
            卡片標題
          </Typography>
          <Typography variant="body">
            這裡是 DashboardCard 內容區。
          </Typography>
        </DashboardCard>
      </Section>
      <Section title="DataCardLayout">
        <DataCardLayout
          topRowSection={
            <React.Fragment>
              <Badge tone="green">
                分類
              </Badge>
              <Badge tone="blue">
                狀態
              </Badge>
            </React.Fragment>
          }
          mainInfoSection={
            <Typography variant="body">
              DataCardLayout main info
            </Typography>
          }
          actionSection={
            <ActionButton
              variant="outline"
              onClick={() => null}
              label="查看"
            />
          }
        />
      </Section>
      <Section title="DateInput">
        <DateInput
          yearRoc={yearRoc}
          month={month}
          day={day}
          onChangeYearRoc={value => setYearRoc(value)}
          onChangeMonth={value => setMonth(value)}
          onChangeDay={value => setDay(value)}
        />
      </Section>
      <Section title="Dialog">
        <ActionButton
          onClick={() => setDialogOpen(true)}
          label="打開 Dialog"
        />
        <Dialog
          isOpen={dialogOpen}
          onBackdropClick={() => setDialogOpen(false)}
        >
          <div className="p-6 flex flex-col gap-3">
            <Typography variant="subtitle">
              Dialog 標題
            </Typography>
            <Typography variant="body">
              這裡是 Dialog 內容
            </Typography>
            <ActionButton
              variant="outline"
              onClick={() => setDialogOpen(false)}
              label="關閉"
            />
          </div>
        </Dialog>
      </Section>
      <Section title="Divider">
        <Divider />
      </Section>
      <Section title="Dropdown">
        <Dropdown
          className={"flex flex-1"}
          hasDiv={false}
          options={REGIONS}
          dataSelected={dropdownValue}
          disabled={false}
          onChange={e => setDropdownValue(e.target.value)}
          text=""
        />
      </Section>
      <Section title="FilterBox">
        <FilterBox label="Label" onClick={() => null} isShowSelect={false} selectedItems={['a']} />
      </Section>
      <Section title="GroupBadge">
        <div className="flex flex-row gap-2">
          <GroupBadge groupName="homeMedicalCare"/>
          <GroupBadge groupName="homeDentalCare"/>
          <GroupBadge groupName="homeNursingCare"/>
          <GroupBadge groupName="mobilePharmacist"/>
          <GroupBadge groupName="mobileDietitian"/>
          <GroupBadge groupName="mobilePsychologist"/>
          <GroupBadge groupName="mobileRehabilitation"/>
          <GroupBadge groupName="homeServices"/>
        </div>
      </Section>
      <Section title="HighlightedText">
        <h3 className="text-gray-800">
          {HighlightedText({content: "once upon a time, there was a fox", keyword: "time"})}
        </h3>
      </Section>
      <Section title="LinkButton">
        <LinkButton
          label="前往連結"
          onClick={() => window.open('https://www.google.com', '_blank')}
        />
      </Section>
      <Section title="LoadingView">
        <LoadingView />
      </Section>
      <Section title="OptionButton">
        <div className="flex flex-row gap-2">
          <OptionButton isSelected={false} onClick={() => null} label={"張協建"}/>
          <OptionButton isSelected={true} onClick={() => null} label={"龔為城"}/>
        </div>
      </Section>
      <Section title="OrganizationCard">
        <OrganizationCard
          imageUrl={DEFAULT_COVER_IMAGE}
          categories={['西醫']}
          title={"標題"}
          region={"地區"}
          address={"地址"}
          phone={"電話"}
          url={"網址"}
        />
      </Section>
      <Section title="Paragraph">
        <Paragraph text={"第一段文字。\n第二段文字。"} />
      </Section>
      <Section title="PhoneInput">
        <PhoneInput
          phone={phone}
          prefix={phonePrefix}
          onChangePhone={value => setPhone(value)}
          onChangePrefix={value => setPhonePrefix(value)}
        />
      </Section>
      <Section title="RoleBadge">
        <div className="flex flex-row gap-2">
          <RoleBadge roleValue="god" />
          <RoleBadge roleValue="admin" />
          <RoleBadge roleValue="worker" />
        </div>
      </Section>
      <Section title="SearchBar">
        <SearchBar placeholder="" value={baseInputValue} onChangeValue={(value) => setBaseInputValue(value)} />
      </Section>
      <Section title="StatusSelector">
        <StatusSelector selectedStatus={'processing'} onChangeSelectedStatus={() => null} />
      </Section>
      <Section title="Tooltip">
        <Tooltip content={"說明說明"}>
          文字文字
        </Tooltip>
      </Section>
      <Section title="TwoButtonDialog">
        <TwoButtonDialog
          type="warning"
          title="這是 TwoButtonDialog"
          leftLabel="確認"
          leftOnClick={() => null}
          rightLabel="取消"
          rightOnClick={() => null}
        />
      </Section>
      <Section title="VaccineButtons">
        <VaccineButtons selected={["M痘疫苗"]} onChangeSelected={() => null} />
      </Section>
    </React.Fragment>
  )

  const renderSharedComponentsBoard = () => (
    <React.Fragment>
      <Section title="components/ Catalog">
        <CatalogList items={SHARED_COMPONENTS} />
      </Section>
    </React.Fragment>
  )

  const renderWidgetsBoard = () => (
    <React.Fragment>
      <Section title="widgets/ Catalog">
        <CatalogList items={SHARED_WIDGETS} />
      </Section>
    </React.Fragment>
  )

  return <StandardLayout
    headerTitle="UI 元件庫"
    body={
      <div className="w-full md:w-[850px] max-w-full bg-white rounded-lg m-auto overflow-hidden p-4">
        <div className="flex flex-row flex-wrap gap-2 pb-4 border-b border-gray-200">
          <ActionButton
            label="designSystem/components"
            variant={boardTab === 'designSystem' ? 'filled' : 'outline'}
            onClick={() => setBoardTab('designSystem')}
          />
          <ActionButton
            label="components"
            variant={boardTab === 'components' ? 'filled' : 'outline'}
            onClick={() => setBoardTab('components')}
          />
          <ActionButton
            label="widgets"
            variant={boardTab === 'widgets' ? 'filled' : 'outline'}
            onClick={() => setBoardTab('widgets')}
          />
        </div>

        {boardTab === 'designSystem' && renderDesignSystemBoard()}
        {boardTab === 'components' && renderSharedComponentsBoard()}
        {boardTab === 'widgets' && renderWidgetsBoard()}
      </div>
    }
  />
}
